import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router';

/**
 * Full-screen chart mode — a SHELL capability any page can request.
 *
 * A page that owns a chart calls `useFullscreenRegion(info)`; while that page is
 * mounted the mode is available: `F` toggles it, `Esc` exits, and the page (or the
 * shell) offers a button. Nav, breadcrumb and page chrome collapse; a compact bar
 * survives showing instrument, session and the active shortcuts. Leaving the route
 * ends the mode. B1's labelling chart and F1's backtest review both consume this.
 */
export interface FullscreenShortcut {
  key: string;
  label: string;
}

export interface FullscreenInfo {
  instrument: string;
  session: string;
  /** Page-level shortcuts to advertise in the compact bar. `F` and `Esc` are added by the shell. */
  shortcuts?: readonly FullscreenShortcut[];
}

interface FullscreenValue {
  /** A page has registered a region: the mode can be entered. */
  available: boolean;
  active: boolean;
  info: FullscreenInfo | null;
  enter: () => void;
  exit: () => void;
  toggle: () => void;
  /** @internal registration from useFullscreenRegion */
  register: (info: FullscreenInfo) => () => void;
}

const FullscreenContext = createContext<FullscreenValue | null>(null);

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.matches('input, select, textarea, [contenteditable="true"]') || target.isContentEditable
  );
}

export function FullscreenProvider({ children }: { children: ReactNode }) {
  const [info, setInfo] = useState<FullscreenInfo | null>(null);
  const [active, setActive] = useState(false);
  const { pathname } = useLocation();
  const available = info !== null;

  // Leaving the route that registered the region ends the mode.
  const lastPath = useRef(pathname);
  useEffect(() => {
    if (lastPath.current !== pathname) {
      lastPath.current = pathname;
      setActive(false);
    }
  }, [pathname]);

  const register = useCallback((next: FullscreenInfo) => {
    setInfo(next);
    return () => {
      setInfo(null);
      setActive(false);
    };
  }, []);

  const enter = useCallback(() => {
    if (available) setActive(true);
  }, [available]);
  const exit = useCallback(() => setActive(false), []);
  const toggle = useCallback(() => {
    if (available) setActive((a) => !a);
  }, [available]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'Escape' && active) {
        e.preventDefault();
        setActive(false);
        return;
      }
      if ((e.key === 'f' || e.key === 'F') && available && !isTypingTarget(e.target)) {
        e.preventDefault();
        setActive((a) => !a);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, available]);

  const value = useMemo<FullscreenValue>(
    () => ({ available, active, info, enter, exit, toggle, register }),
    [available, active, info, enter, exit, toggle, register],
  );

  return <FullscreenContext.Provider value={value}>{children}</FullscreenContext.Provider>;
}

export function useFullscreen(): FullscreenValue {
  const ctx = useContext(FullscreenContext);
  if (!ctx) throw new Error('useFullscreen must be used inside <FullscreenProvider>');
  return ctx;
}

/**
 * Declare that this page has a full-screen-capable chart. Re-registers when the
 * instrument / session change so the compact bar stays truthful.
 */
export function useFullscreenRegion(info: FullscreenInfo): FullscreenValue {
  const fs = useFullscreen();
  const { instrument, session } = info;
  const shortcutsKey = JSON.stringify(info.shortcuts ?? []);
  const register = fs.register;
  useEffect(
    () =>
      register({
        instrument,
        session,
        shortcuts: JSON.parse(shortcutsKey) as FullscreenShortcut[],
      }),
    [register, instrument, session, shortcutsKey],
  );
  return fs;
}
